import { normalizeForSearch } from "@/lib/search/normalize"

export function Highlight({
  text,
  tokens,
}: {
  text: string
  tokens: string[]
}) {
  if (tokens.length === 0) return <>{text}</>

  // Build offsets in the ORIGINAL string by scanning the normalized one
  // and tracking a parallel cursor. Diacritic stripping is 1-to-1 in our
  // normalize function (NFD + diacritic removal preserves base char count
  // for the locales we support), so a per-character cursor works.
  const norm = normalizeForSearch(text)

  type Range = { start: number; end: number }
  const ranges: Range[] = []
  for (const tok of tokens) {
    if (!tok) continue
    let from = 0
    while (true) {
      const idx = norm.indexOf(tok, from)
      if (idx === -1) break
      ranges.push({ start: idx, end: idx + tok.length })
      from = idx + tok.length
    }
  }

  if (ranges.length === 0) return <>{text}</>

  // Merge overlapping ranges
  ranges.sort((a, b) => a.start - b.start)
  const merged: Range[] = [ranges[0]]
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1]
    if (ranges[i].start <= last.end) {
      last.end = Math.max(last.end, ranges[i].end)
    } else {
      merged.push(ranges[i])
    }
  }

  // Render — slice the ORIGINAL text using normalized offsets.
  const out: (string | React.ReactElement)[] = []
  let cursor = 0
  merged.forEach((r, i) => {
    if (r.start > cursor) out.push(text.slice(cursor, r.start))
    out.push(
      <mark key={i} className="rounded bg-yellow-200 px-0.5 text-neutral-900">
        {text.slice(r.start, r.end)}
      </mark>
    )
    cursor = r.end
  })
  if (cursor < text.length) out.push(text.slice(cursor))
  return <>{out}</>
}
